/**
 * db.js — All Supabase database operations
 *
 * Drop-in replacement for firestore.js with identical function signatures.
 * All functions take a `uid` parameter (Supabase auth user ID) to scope data.
 *
 * Tables (all user-scoped via RLS):
 *   users, bills, accounts, transactions, categories, budget_periods,
 *   goals, alerts, net_worth_snapshots, subscription_suggestions,
 *   income_suggestions, income_sources, teller_enrollments
 */

import { supabase } from '../supabase';
import { normalizeMerchant, suggestCategory, suggestFromMerchantName, CATEGORY_BUCKET_MAP } from '../utils/categories';

const now = () => new Date().toISOString();

// ==================== USER CONFIG ====================

export async function getUserConfig(uid) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', uid)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function saveUserConfig(uid, config) {
  const { error } = await supabase
    .from('users')
    .upsert({ id: uid, ...config, updated_at: now() }, { onConflict: 'id' });
  if (error) throw error;
}

// ==================== BILLS ====================

export async function getBills(uid) {
  const { data, error } = await supabase
    .from('bills')
    .select('*')
    .eq('user_id', uid)
    .order('due_day', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function getActiveBills(uid) {
  const { data, error } = await supabase
    .from('bills')
    .select('*')
    .eq('user_id', uid)
    .neq('active', false)
    .order('due_day', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function addBill(uid, bill) {
  // Whitelist only columns that exist in the bills table
  const { data, error } = await supabase
    .from('bills')
    .insert({
      user_id: uid,
      name: bill.name,
      amount: bill.amount,
      bucket: bill.bucket || 'need',
      frequency: bill.frequency || 'monthly',
      due_day: bill.due_day || null,
      auto_pay: bill.auto_pay || false,
      is_subscription: bill.is_subscription || false,
      variable: bill.variable || false,
      match_pattern: bill.match_pattern || null,
      active: bill.active !== false,
      current_cycle_paid: bill.current_cycle_paid || false,
      current_cycle_paid_amount: bill.current_cycle_paid_amount || 0,
      created_at: now(),
      updated_at: now(),
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function updateBill(uid, billId, updates) {
  const { error } = await supabase
    .from('bills')
    .update({ ...updates, updated_at: now() })
    .eq('id', billId)
    .eq('user_id', uid);
  if (error) throw error;
}

export async function deleteBill(uid, billId) {
  const { error } = await supabase
    .from('bills')
    .delete()
    .eq('id', billId)
    .eq('user_id', uid);
  if (error) throw error;
}

export async function toggleBillPaid(uid, billId, paid, paidAmount) {
  const updates = { current_cycle_paid: paid, updated_at: now() };
  if (paidAmount !== undefined) updates.current_cycle_paid_amount = paidAmount;
  const { error } = await supabase
    .from('bills')
    .update(updates)
    .eq('id', billId)
    .eq('user_id', uid);
  if (error) throw error;
}

export async function resetBillCycles(uid) {
  const { data, error } = await supabase
    .from('bills')
    .update({ current_cycle_paid: false, current_cycle_paid_amount: 0, updated_at: now() })
    .eq('user_id', uid)
    .eq('current_cycle_paid', true)
    .select('id');
  if (error) throw error;
  return data?.length || 0;
}

export async function seedBills(uid, bills) {
  const existing = await getBills(uid);
  const existingNames = new Set(existing.map(b => b.name.toLowerCase()));
  const toInsert = bills
    .filter(b => !existingNames.has(b.name.toLowerCase()))
    .map(b => ({
      user_id: uid,
      ...b,
      active: true,
      current_cycle_paid: false,
      current_cycle_paid_amount: 0,
      created_at: now(),
      updated_at: now(),
    }));
  if (!toInsert.length) return 0;
  const { error } = await supabase.from('bills').insert(toInsert);
  if (error) throw error;
  return toInsert.length;
}

// ==================== SUBSCRIPTION SUGGESTIONS ====================

export async function getSubscriptionSuggestions(uid) {
  const { data, error } = await supabase
    .from('subscription_suggestions')
    .select('*')
    .eq('user_id', uid)
    .eq('status', 'pending');
  if (error) throw error;
  return data || [];
}

export async function acceptSuggestion(uid, suggestionId) {
  const { data: sug, error: sugErr } = await supabase
    .from('subscription_suggestions')
    .select('*')
    .eq('id', suggestionId)
    .eq('user_id', uid)
    .single();
  if (sugErr) throw sugErr;

  const billId = await addBill(uid, {
    name: sug.merchant,
    amount: sug.amount,
    bucket: 'want',
    frequency: sug.frequency || 'monthly',
    due_day: 1,
    auto_pay: true,
    is_subscription: true,
    variable: false,
    match_pattern: sug.match_pattern || '',
  });

  const { error } = await supabase
    .from('subscription_suggestions')
    .update({ status: 'accepted', accepted_bill_id: billId, updated_at: now() })
    .eq('id', suggestionId)
    .eq('user_id', uid);
  if (error) throw error;
  return billId;
}

export async function dismissSuggestion(uid, suggestionId) {
  const { error } = await supabase
    .from('subscription_suggestions')
    .update({ status: 'dismissed', updated_at: now() })
    .eq('id', suggestionId)
    .eq('user_id', uid);
  if (error) throw error;
}

// ==================== ACCOUNTS ====================

export async function getAccounts(uid) {
  const { data, error } = await supabase
    .from('accounts')
    .select('*')
    .eq('user_id', uid)
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function toggleAccountSync(accountId, includeInSync) {
  const { error } = await supabase
    .from('accounts')
    .update({ include_in_sync: includeInSync })
    .eq('id', accountId);
  if (error) throw error;
}

export async function getPrimaryCheckingBalance(uid, mask = null) {
  const accounts = await getAccounts(uid);
  const primary = (mask ? accounts.find(a => a.type === 'checking' && a.mask === mask) : null)
    || accounts.find(a => a.is_primary)
    || accounts.find(a => a.type === 'checking');
  return primary?.current_balance || 0;
}

// ==================== CATEGORIES ====================

export async function getCategoryOverrides(uid) {
  const { data, error } = await supabase
    .from('categories')
    .select('name, bucket')
    .eq('user_id', uid);
  if (error) throw error;
  const overrides = {};
  (data || []).forEach(row => { overrides[row.name] = row.bucket; });
  return overrides;
}

export async function saveCategoryOverride(uid, category, bucket) {
  const { error } = await supabase
    .from('categories')
    .upsert(
      { user_id: uid, name: category, bucket, updated_at: now() },
      { onConflict: 'user_id,name' }
    );
  if (error) throw error;
}

// ==================== BUDGET PERIODS ====================

export async function getBudgetPeriod(uid, monthKey) {
  const { data, error } = await supabase
    .from('budget_periods')
    .select('*')
    .eq('user_id', uid)
    .eq('month_key', monthKey)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function saveBudgetPeriod(uid, monthKey, data) {
  const { error } = await supabase
    .from('budget_periods')
    .upsert(
      { user_id: uid, month_key: monthKey, ...data, updated_at: now() },
      { onConflict: 'user_id,month_key' }
    );
  if (error) throw error;
}

export async function getBudgetHistory(uid, months = 6) {
  const { getMonthKey, shiftMonthKey } = await import('../utils/format');
  const currentMonth = getMonthKey();
  const startMonth = shiftMonthKey(currentMonth, -(months - 1));
  const { data, error } = await supabase
    .from('budget_periods')
    .select('*')
    .eq('user_id', uid)
    .gte('month_key', startMonth)
    .lte('month_key', currentMonth)
    .order('month_key', { ascending: true });
  if (error) throw error;
  return (data || []).map(row => ({ monthKey: row.month_key, ...row }));
}

export async function getBudgetHistoryRange(uid, startMonth, endMonth) {
  const { data, error } = await supabase
    .from('budget_periods')
    .select('*')
    .eq('user_id', uid)
    .gte('month_key', startMonth)
    .lte('month_key', endMonth)
    .order('month_key', { ascending: true });
  if (error) throw error;
  return (data || []).map(row => ({ monthKey: row.month_key, ...row }));
}

export async function getIncomeSurplus(uid, monthKey) {
  const data = await getBudgetPeriod(uid, monthKey);
  if (!data) return null;
  return {
    income_expected: data.income_expected || 0,
    income_actual: data.income_actual || 0,
    income_surplus: data.income_surplus || 0,
    surplus_allocated: data.surplus_allocated || false,
    surplus_allocation: data.surplus_allocation || null,
  };
}

export async function saveSurplusAllocation(uid, monthKey, allocation) {
  const total = (allocation.need || 0) + (allocation.want || 0) + (allocation.savings || 0);
  await saveBudgetPeriod(uid, monthKey, {
    surplus_allocated: true,
    surplus_allocation: {
      need: allocation.need || 0,
      want: allocation.want || 0,
      savings: allocation.savings || 0,
      unallocated: 0,
      total,
    },
  });
}

// ==================== NET WORTH SNAPSHOTS ====================

export async function getNetWorthSnapshots(uid, months = 12) {
  const { getMonthKey, shiftMonthKey } = await import('../utils/format');
  const currentMonth = getMonthKey();
  const startMonth = shiftMonthKey(currentMonth, -(months - 1));
  const { data, error } = await supabase
    .from('net_worth_snapshots')
    .select('*')
    .eq('user_id', uid)
    .gte('month_key', startMonth)
    .lte('month_key', currentMonth)
    .order('month_key', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function getNetWorthSnapshotsRange(uid, startMonth, endMonth) {
  const { data, error } = await supabase
    .from('net_worth_snapshots')
    .select('*')
    .eq('user_id', uid)
    .gte('month_key', startMonth)
    .lte('month_key', endMonth)
    .order('month_key', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ==================== GOALS ====================

export async function getGoals(uid) {
  const { data, error } = await supabase
    .from('goals')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getActiveGoals(uid) {
  const { data, error } = await supabase
    .from('goals')
    .select('*')
    .eq('user_id', uid)
    .neq('active', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function addGoal(uid, goal) {
  const { data, error } = await supabase
    .from('goals')
    .insert({
      user_id: uid,
      ...goal,
      current_amount: goal.current_amount || 0,
      active: true,
      created_at: now(),
      updated_at: now(),
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function updateGoal(uid, goalId, updates) {
  const { error } = await supabase
    .from('goals')
    .update({ ...updates, updated_at: now() })
    .eq('id', goalId)
    .eq('user_id', uid);
  if (error) throw error;
}

export async function deleteGoal(uid, goalId) {
  const { error } = await supabase
    .from('goals')
    .delete()
    .eq('id', goalId)
    .eq('user_id', uid);
  if (error) throw error;
}

export async function contributeToGoal(uid, goalId, amount) {
  const { data: goal, error: fetchErr } = await supabase
    .from('goals')
    .select('current_amount')
    .eq('id', goalId)
    .eq('user_id', uid)
    .single();
  if (fetchErr) throw fetchErr;
  const newAmount = (goal.current_amount || 0) + amount;
  const { error } = await supabase
    .from('goals')
    .update({ current_amount: newAmount, updated_at: now() })
    .eq('id', goalId)
    .eq('user_id', uid);
  if (error) throw error;
  return newAmount;
}

// ==================== ALERTS ====================

export async function getAlerts(uid, count = 10) {
  const { data, error } = await supabase
    .from('alerts')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false })
    .limit(count);
  if (error) throw error;
  return data || [];
}

export async function markAlertRead(uid, alertId) {
  const { error } = await supabase
    .from('alerts')
    .update({ read: true })
    .eq('id', alertId)
    .eq('user_id', uid);
  if (error) throw error;
}

export async function markAllAlertsRead(uid) {
  const { data, error } = await supabase
    .from('alerts')
    .update({ read: true })
    .eq('user_id', uid)
    .eq('read', false)
    .select('id');
  if (error) throw error;
  return data?.length || 0;
}

// ==================== TRANSACTIONS ====================

export async function getTransactionsByMonth(uid, monthKey) {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('user_id', uid)
    .eq('month_key', monthKey)
    .order('date', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getRecentTransactions(uid, count = 10) {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('user_id', uid)
    .order('date', { ascending: false })
    .limit(count);
  if (error) throw error;
  return data || [];
}

// ==================== REVIEW QUEUE ====================

export async function getReviewQueue(uid, cap = 10) {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('user_id', uid)
    .eq('needs_review', true)
    .order('date', { ascending: false })
    .limit(cap);
  if (error) throw error;
  return data || [];
}

export async function getReviewQueueCount(uid) {
  const { count, error } = await supabase
    .from('transactions')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', uid)
    .eq('needs_review', true);
  if (error) throw error;
  return count || 0;
}

export async function dismissFromReviewQueue(uid, transactionId, category) {
  const updates = { needs_review: false, reviewed_at: now(), updated_at: now() };
  if (category) {
    updates.category = category;
    updates.confidence = 100;
    updates.confidence_source = 'user_correction';
  }
  const { error } = await supabase
    .from('transactions')
    .update(updates)
    .eq('id', transactionId)
    .eq('user_id', uid);
  if (error) throw error;
}

/**
 * Assign a bucket to a transaction and save a merchant rule so future
 * transactions from the same description get auto-categorized.
 */
export async function categorizeTxn(uid, transactionId, bucket, description) {
  const updates = [
    // Update this specific transaction
    supabase.from('transactions').update({
      bucket,
      needs_review: false,
      reviewed_at: now(),
      confidence: 100,
      confidence_source: 'user_correction',
      updated_at: now(),
    }).eq('id', transactionId).eq('user_id', uid),
  ];

  if (description) {
    updates.push(
      // Save merchant rule for future syncs
      supabase.from('categories').upsert(
        { user_id: uid, name: description, bucket, updated_at: now() },
        { onConflict: 'user_id,name' }
      ),
      // Retroactively apply rule to all matching transactions still in review
      supabase.from('transactions').update({
        bucket,
        needs_review: false,
        reviewed_at: now(),
        confidence: 100,
        confidence_source: 'user_correction',
        updated_at: now(),
      }).eq('user_id', uid).eq('description', description).eq('needs_review', true),
    );
  }

  await Promise.all(updates);
}

/**
 * Returns the merchant review queue — groups all needs_review debit transactions
 * by normalized merchant name, sorted by count descending.
 * Merchants that already have a saved rule are excluded (applyExistingRules handles them).
 */
export async function getMerchantReviewQueue(uid) {
  const [{ data: txns, error: txnErr }, { data: rules, error: ruleErr }] = await Promise.all([
    supabase
      .from('transactions')
      .select('id, description, amount, date, source_category, category')
      .eq('user_id', uid)
      .eq('needs_review', true)
      .eq('is_credit', false),
    supabase
      .from('categories')
      .select('name, category_label, bucket')
      .eq('user_id', uid),
  ]);
  if (txnErr) throw txnErr;
  if (ruleErr) throw ruleErr;

  // Build ruleMap keyed by normalized merchant name
  const ruleMap = {};
  for (const r of (rules || [])) {
    ruleMap[normalizeMerchant(r.name)] = { categoryLabel: r.category_label, bucket: r.bucket };
  }

  // Group transactions by normalized merchant
  const groups = {};
  for (const txn of (txns || [])) {
    const key = normalizeMerchant(txn.description);
    if (!groups[key]) groups[key] = [];
    groups[key].push(txn);
  }

  const result = [];
  for (const [merchantKey, merchantTxns] of Object.entries(groups)) {
    if (ruleMap[merchantKey]) continue; // existing rule — applyExistingRules handles it

    const totalAmount = merchantTxns.reduce((s, t) => s + (t.amount || 0), 0);
    const dates = merchantTxns.map(t => t.date).filter(Boolean).sort();
    const firstTxn = merchantTxns[0];
    const suggestedCat = suggestCategory(firstTxn.source_category || firstTxn.category)
      || suggestFromMerchantName(merchantKey);

    result.push({
      merchantKey,
      sampleDescription: firstTxn.description || merchantKey,
      count: merchantTxns.length,
      totalAmount,
      dateRange: dates.length >= 2 ? `${dates[0]} – ${dates[dates.length - 1]}` : (dates[0] || ''),
      suggestedCategory: suggestedCat,
      suggestedBucket: suggestedCat ? (CATEGORY_BUCKET_MAP[suggestedCat.toLowerCase()] || 'want') : null,
      transactionIds: merchantTxns.map(t => t.id),
    });
  }

  result.sort((a, b) => b.count - a.count);
  return result;
}

/**
 * Saves a merchant-level category rule and bulk-updates all matched transactions.
 * Uses transaction IDs (not ILIKE) to avoid false positives.
 */
export async function categorizeMerchant(uid, merchantKey, categoryLabel, bucket, transactionIds) {
  const { error: ruleErr } = await supabase
    .from('categories')
    .upsert(
      { user_id: uid, name: merchantKey, category_label: categoryLabel, bucket, updated_at: now() },
      { onConflict: 'user_id,name' }
    );
  if (ruleErr) throw ruleErr;

  for (let i = 0; i < transactionIds.length; i += 100) {
    const batch = transactionIds.slice(i, i + 100);
    const { error } = await supabase
      .from('transactions')
      .update({
        bucket,
        category: categoryLabel,
        needs_review: false,
        reviewed_at: now(),
        confidence: 100,
        confidence_source: 'user_merchant_rule',
        updated_at: now(),
      })
      .eq('user_id', uid)
      .in('id', batch);
    if (error) throw error;
  }
}

/**
 * Applies existing merchant rules to all current needs_review transactions.
 * Call before getMerchantReviewQueue to pre-clear already-known merchants.
 * Returns the count of transactions auto-applied.
 */
export async function applyExistingRules(uid) {
  const [{ data: rules, error: ruleErr }, { data: txns, error: txnErr }] = await Promise.all([
    supabase.from('categories').select('name, category_label, bucket').eq('user_id', uid),
    supabase.from('transactions').select('id, description')
      .eq('user_id', uid).eq('needs_review', true).eq('is_credit', false),
  ]);
  if (ruleErr) throw ruleErr;
  if (txnErr) throw txnErr;

  if (!rules?.length || !txns?.length) return 0;

  const ruleMap = {};
  for (const r of rules) {
    ruleMap[normalizeMerchant(r.name)] = { categoryLabel: r.category_label, bucket: r.bucket };
  }

  // Group matched transactions by their rule outcome
  const matchGroups = {};
  for (const txn of txns) {
    const rule = ruleMap[normalizeMerchant(txn.description)];
    if (!rule) continue;
    const groupKey = `${rule.bucket}::${rule.categoryLabel || ''}`;
    if (!matchGroups[groupKey]) matchGroups[groupKey] = { ...rule, ids: [] };
    matchGroups[groupKey].ids.push(txn.id);
  }

  let totalApplied = 0;
  for (const { bucket, categoryLabel, ids } of Object.values(matchGroups)) {
    for (let i = 0; i < ids.length; i += 100) {
      const batch = ids.slice(i, i + 100);
      const updates = {
        bucket,
        needs_review: false,
        reviewed_at: now(),
        confidence: 100,
        confidence_source: 'user_merchant_rule',
        updated_at: now(),
      };
      if (categoryLabel) updates.category = categoryLabel;

      const { error } = await supabase
        .from('transactions')
        .update(updates)
        .eq('user_id', uid)
        .in('id', batch);
      if (error) throw error;
      totalApplied += batch.length;
    }
  }
  return totalApplied;
}

export async function flagForReview(uid, transactionId, reason = 'manual') {
  const { error } = await supabase
    .from('transactions')
    .update({ needs_review: true, review_reason: reason, updated_at: now() })
    .eq('id', transactionId)
    .eq('user_id', uid);
  if (error) throw error;
}

// ==================== INCOME SUGGESTIONS ====================

export async function getIncomeSuggestions(uid) {
  const { data, error } = await supabase
    .from('income_suggestions')
    .select('*')
    .eq('user_id', uid)
    .eq('status', 'pending');
  if (error) throw error;
  return data || [];
}

export async function acceptIncomeSuggestion(uid, suggestionId) {
  const { data: sug, error: sugErr } = await supabase
    .from('income_suggestions')
    .select('*')
    .eq('id', suggestionId)
    .eq('user_id', uid)
    .single();
  if (sugErr) throw sugErr;

  const sourceId = await addIncomeSource(uid, {
    name: sug.description,
    match_pattern: sug.match_pattern,
    expected_amount: sug.variable ? 0 : sug.avg_amount,
    frequency: sug.frequency || 'monthly',
    anchor_date: '',
    variable: sug.variable || false,
    is_primary: false,
  });

  const { error } = await supabase
    .from('income_suggestions')
    .update({ status: 'accepted', accepted_source_id: sourceId, updated_at: now() })
    .eq('id', suggestionId)
    .eq('user_id', uid);
  if (error) throw error;
  return sourceId;
}

export async function dismissIncomeSuggestion(uid, suggestionId) {
  const { error } = await supabase
    .from('income_suggestions')
    .update({ status: 'dismissed', updated_at: now() })
    .eq('id', suggestionId)
    .eq('user_id', uid);
  if (error) throw error;
}

// ==================== INCOME SOURCES ====================

export async function getIncomeSources(uid) {
  const { data, error } = await supabase
    .from('income_sources')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function addIncomeSource(uid, source) {
  const { data, error } = await supabase
    .from('income_sources')
    .insert({ user_id: uid, ...source, active: true, created_at: now(), updated_at: now() })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function updateIncomeSource(uid, sourceId, updates) {
  const { error } = await supabase
    .from('income_sources')
    .update({ ...updates, updated_at: now() })
    .eq('id', sourceId)
    .eq('user_id', uid);
  if (error) throw error;
}

export async function deleteIncomeSource(uid, sourceId) {
  const { error } = await supabase
    .from('income_sources')
    .delete()
    .eq('id', sourceId)
    .eq('user_id', uid);
  if (error) throw error;
}

// ==================== TELLER ENROLLMENTS ====================

export async function getTellerEnrollments(uid) {
  const { data, error } = await supabase
    .from('teller_enrollments')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ==================== USER CATEGORY DEFS ====================

export async function getUserCategoryDefs(uid) {
  const { data, error } = await supabase
    .from('user_category_defs')
    .select('*')
    .eq('user_id', uid)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function saveUserCategory(uid, { id, label, bucket, sort_order = 0 }) {
  if (id) {
    const { error } = await supabase
      .from('user_category_defs')
      .update({ label, bucket, sort_order })
      .eq('id', id)
      .eq('user_id', uid);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('user_category_defs')
      .insert({ user_id: uid, label, bucket, sort_order });
    if (error) throw error;
  }
}

export async function deleteUserCategory(uid, id) {
  const { error } = await supabase
    .from('user_category_defs')
    .delete()
    .eq('id', id)
    .eq('user_id', uid);
  if (error) throw error;
}

/** Bulk-insert from CATEGORY_TAXONOMY on first customization — ignores duplicates. */
export async function initUserCategoryDefs(uid, defs) {
  const rows = defs.map((d, i) => ({
    user_id: uid, label: d.label, bucket: d.bucket, sort_order: i,
  }));
  const { error } = await supabase
    .from('user_category_defs')
    .upsert(rows, { onConflict: 'user_id,label', ignoreDuplicates: true });
  if (error) throw error;
}

export async function deleteAllUserCategoryDefs(uid) {
  const { error } = await supabase
    .from('user_category_defs')
    .delete()
    .eq('user_id', uid);
  if (error) throw error;
}

// ==================== BUCKET NAMES ====================

const DEFAULT_BUCKET_NAMES = { need: 'Needs', want: 'Wants', savings: 'Savings', income: 'Income' };

export async function getUserBucketNames(uid) {
  const { data, error } = await supabase
    .from('users')
    .select('bucket_names')
    .eq('id', uid)
    .maybeSingle();
  if (error) throw error;
  return { ...DEFAULT_BUCKET_NAMES, ...(data?.bucket_names || {}) };
}

export async function saveUserBucketNames(uid, names) {
  const { error } = await supabase
    .from('users')
    .upsert({ id: uid, bucket_names: names, updated_at: now() }, { onConflict: 'id' });
  if (error) throw error;
}
